import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ReceiptText, Server, RadioTower, ArrowUpRight, Zap, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtMonth, fmtNum, TOOL_META } from "@/lib/format";
import { KpiCard, SectionCard } from "@/components/common/Cards";

const TOOL_ICON = { pajak: ReceiptText, hosting: Server, isp: RadioTower, admin_test: Sparkles };

export default function UsagePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/ai/usage/me")
      .then(({ data }) => setData(data))
      .catch(() => setData({}));
  }, []);

  if (!data) {
    return (
      <div className="max-w-[1200px] mx-auto space-y-4" data-testid="usage-loading">
        <Skeleton className="h-24 bg-[hsl(220_10%_11%)]" />
        <div className="grid md:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 bg-[hsl(220_10%_11%)]" />
          ))}
        </div>
        <Skeleton className="h-80 bg-[hsl(220_10%_11%)]" />
      </div>
    );
  }

  const pct = data.limit ? Math.min(100, (data.used / data.limit) * 100) : 0;
  const nearLimit = pct >= 80;
  const over = pct >= 100;
  const recent = data.recent || [];
  const byTool = data.by_tool || [];
  const trend = data.trend || [];

  return (
    <div className="max-w-[1200px] mx-auto space-y-6">
      <div>
        <div className="eyebrow flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> AI Usage · {fmtMonth(data.month)}
        </div>
        <h1 className="font-display text-4xl mt-1.5" data-testid="usage-page-title">
          Pemakaian Token AI
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">
          Setiap tombol <b className="text-foreground">Rekomendasi AI</b> memakai token. Bulan ini Anda memiliki kuota {fmtNum(data.limit)} token.
        </p>
      </div>

      <section
        className={`rounded-2xl border p-6 elev-1 ${
          over
            ? "border-[hsl(0_60%_40%)] bg-[hsl(0_40%_8%)]"
            : nearLimit
            ? "border-[hsl(38_90%_40%)] bg-[hsl(38_40%_10%)]"
            : "border-[color:var(--hairline)] bg-[hsl(43_30%_7%)]"
        }`}
        data-testid="usage-summary-card"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Kuota bulan ini</div>
            <div className="num text-4xl sm:text-5xl mt-2 text-foreground" data-testid="usage-used-value">
              {fmtNum(data.used)}
              <span className="text-xl text-muted-foreground"> / {fmtNum(data.limit)}</span>
            </div>
            <Progress value={pct} className="h-2 mt-3 bg-[hsl(220_10%_14%)] max-w-[420px]" data-testid="usage-progress" />
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <Badge variant="outline" className="border-border num">
                Sisa {fmtNum(Math.max(0, data.limit - data.used))} token
              </Badge>
              <Badge variant="outline" className="border-border">
                Model: <span className="ml-1 num">{data.model}</span>
              </Badge>
              <Badge variant="outline" className="border-border">
                Sumber: {data.key_source === "own" ? "API key sendiri" : "Emergent Universal"}
              </Badge>
            </div>
          </div>
          {over && (
            <div className="max-w-sm text-sm text-[hsl(0_80%_80%)]" data-testid="usage-over-limit-notice">
              <b>Kuota habis.</b> Tombol Rekomendasi AI akan tertolak sampai bulan berikutnya atau admin menaikkan batas.
            </div>
          )}
          {!over && nearLimit && (
            <div className="max-w-sm text-sm text-[hsl(38_90%_80%)]" data-testid="usage-near-limit-notice">
              Pemakaian sudah di atas 80%. Pertimbangkan efisiensi atau minta admin menaikkan batas.
            </div>
          )}
        </div>
      </section>

      <div className="grid md:grid-cols-3 gap-3">
        <KpiCard
          label="Pemakaian bulan ini"
          value={fmtNum(data.used)}
          sub={`${fmtNum(data.limit)} token kuota`}
          icon={Zap}
          accent
          testid="usage-kpi-used"
        />
        <KpiCard
          label="Jumlah request AI"
          value={fmtNum(recent.length && trend.find((t) => t.month === data.month)?.requests ? trend.find((t) => t.month === data.month).requests : recent.filter((r) => r.month === data.month).length)}
          sub="Pada bulan berjalan"
          icon={Sparkles}
          testid="usage-kpi-requests"
        />
        <KpiCard
          label="Tool paling sering"
          value={byTool[0] ? TOOL_META[byTool[0].tool]?.short || byTool[0].tool : "–"}
          sub={byTool[0] ? `${fmtNum(byTool[0].tokens)} token` : "Belum ada pemakaian"}
          icon={Zap}
          testid="usage-kpi-top-tool"
        />
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
        <SectionCard title="Rincian per tool" description={`Periode ${fmtMonth(data.month)}`} testid="usage-by-tool-card">
          {byTool.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Belum ada pemakaian bulan ini.</div>
          ) : (
            <div className="space-y-3" data-testid="usage-by-tool-list">
              {byTool.map((t) => {
                const Icon = TOOL_ICON[t.tool] || Sparkles;
                const ratio = data.used ? (t.tokens / data.used) * 100 : 0;
                return (
                  <div key={t.tool} data-testid={`usage-by-tool-${t.tool}`}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{TOOL_META[t.tool]?.label || t.tool}</span>
                      <Badge variant="outline" className="border-border text-muted-foreground text-[10px] num">
                        {fmtNum(t.requests)}x
                      </Badge>
                      <span className="ml-auto text-sm num">{fmtNum(t.tokens)} token</span>
                    </div>
                    <Progress value={ratio} className="h-1.5 mt-2 bg-[hsl(220_10%_13%)]" />
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
        <SectionCard title="Tren 12 bulan terakhir" description="Total token per bulan" testid="usage-trend-card">
          {trend.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Belum ada data.</div>
          ) : (
            <div className="space-y-2" data-testid="usage-trend-list">
              {trend
                .slice()
                .reverse()
                .map((t) => {
                  const max = Math.max(...trend.map((x) => x.tokens), 1);
                  const w = (t.tokens / max) * 100;
                  return (
                    <div key={t.month} className="flex items-center gap-3 text-xs" data-testid={`usage-trend-${t.month}`}>
                      <span className="w-20 text-muted-foreground">{fmtMonth(t.month)}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-[hsl(220_10%_13%)] overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${w}%` }} />
                      </div>
                      <span className="num w-16 text-right">{fmtNum(t.tokens)}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Request terakhir" description="20 pemakaian AI terakhir Anda" testid="usage-recent-card" bodyClassName="p-0">
        {recent.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Belum ada request AI.</div>
        ) : (
          <div className="divide-y divide-border">
            {recent.map((r) => {
              const Icon = TOOL_ICON[r.tool] || Sparkles;
              return (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3 text-sm" data-testid={`usage-recent-${r.id}`}>
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-foreground truncate">{r.question || "Rekomendasi umum"}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      <span className="num">{fmtDate(r.created_at)}</span>
                      <span>· {TOOL_META[r.tool]?.short || r.tool}</span>
                      {r.estimated && <Badge variant="outline" className="border-border text-[9px]">estimasi</Badge>}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="num text-foreground">{fmtNum(r.total_tokens)}</div>
                    <div className="text-muted-foreground text-[10px]">in {fmtNum(r.input_tokens)} · out {fmtNum(r.output_tokens)}</div>
                  </div>
                  {r.calc_id && (
                    <Link
                      to={`${TOOL_META[r.tool]?.path}?id=${r.calc_id}`}
                      className="text-muted-foreground hover:text-primary"
                      title="Buka perhitungan terkait"
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
